import * as React from 'react';
import { compose } from 'recompose';
import b_ from 'b_';
import { createSelector } from 'reselect';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { form, FormProps } from 'shared/utils/form';
import { useTrack } from 'shared/utils/track';
import { validate } from '../../../../utils/validators';
import SubmitButton from '../../../../components/SubmitButton';
import TimerButton from '../../../../components/TimerButton';
import { timeouts, personalData } from '../../selectors';
import { actions as regActions } from '../../actions';
import { routine, codeRoutine, lifecycle } from './actions';
import { constraints, FormData } from './constraints';
import { SendingMethod } from '../../types';
import { Input } from 'shared/components/Input';

import './index.scss';
import { featureFlagsSelector } from 'selectors/config';

export interface Props {}

const selector = createSelector([timeouts, personalData], (timeoutsProps, personalData) => ({
  ...timeoutsProps,
  ...personalData,
}));

const b = b_.with('verification-form');

type Enhanced = Props & FormProps<FormData, Props>;

const enhance = compose<Enhanced, Props>(
  form<FormData, Props>(routine, {
    form: 'fullRegistrationVerification',
    validate: (values) => validate(constraints, values),
  })
);

const VerificationForm: React.FC<Enhanced> = ({ formSubmit, submitting, error }) => {
  const { t } = useTranslation();

  useTrack(lifecycle, {});

  const { codeSource, email, emailCodeTimeout, phone, smsCodeTimeout } = useSelector(selector);
  const { sendRegistrationCodeToEmail } = useSelector(featureFlagsSelector);

  const dispatch = useDispatch();

  const goBack = () => dispatch(regActions.goToStep('PERSONAL_DATA'));
  const sendSMS = () => dispatch(codeRoutine(SendingMethod.PHONE));
  const sendEmail = () => dispatch(codeRoutine(SendingMethod.EMAIL));

  return (
    <form onSubmit={formSubmit} method="POST" data-testid="signup-popup-code-step">
      <div className={b('row')}>
        <Input
          name="verificationCode"
          label={codeSource === SendingMethod.PHONE ? t('Code from sms') : t('Code from email')}
          hint={t('Code sent to {destination}', {
            destination: codeSource === SendingMethod.PHONE ? phone : email,
          })}
          autoComplete="off"
          autoFocus
          tooltipPlacement="bottomLeftSticky"
        />
      </div>
      <div>
        <button className={b('btn')} onClick={goBack} type="button" tabIndex={-1}>
          {t('Back to first step of registration')}
        </button>
        {!sendRegistrationCodeToEmail && (
          <TimerButton
            disabledUntil={smsCodeTimeout}
            onClick={sendSMS}
            className={b('btn')}
            type="button"
            tabIndex={-1}
          >
            {t('Resend code to phone')}
          </TimerButton>
        )}
        <TimerButton
          disabledUntil={emailCodeTimeout}
          onClick={sendEmail}
          className={b('btn')}
          type="button"
          tabIndex={-1}
        >
          {t('Send to email')}
        </TimerButton>
      </div>
      <SubmitButton
        className={b('submit-btn')}
        primary
        submitting={submitting}
        error={error}
        data-gtm="registrationform-secondstep-btn"
      >
        {t('Next')}
      </SubmitButton>
    </form>
  );
};

export default enhance(VerificationForm);
